import { UAParser } from "ua-parser-js";
import { sha256 } from 'js-sha256';
import type { PageViewPayload } from "@/services/view";
import type { PageType } from "@zotlo-shared/types/model";
import { parseValue } from "#shared/utils/session";
import { Facebook, GA4, GTM, Tiktok } from "./integrations";

export function getUserAgent(pageType?: PageType): PageViewPayload {
  const ua = new UAParser().getResult();
  return {
    pageType: pageType as PageType || '',
    platform: ua.device.type || 'desktop',
    browser: ua.browser.name || '',
    browserVersion: ua.browser.version || '',
    osName: ua.os.name || '',
    osVersion: ua.os.version || '',
    deviceModel: ua.device.model || '',
    deviceVendor: ua.device.vendor || ''
  };
}

export function getSessionCookiesOnClient() {
  return Object.values(COOKIE).reduce((acc, cookie) => {
    acc[cookie] = parseValue(getClientCookie(cookie) || '');
    return acc;
  }, {} as Record<COOKIE, string | boolean | number | any[]>);
}

export function triggerRouteChange(routeName: string) {
  if (import.meta.client) {
    window.parent.postMessage({ route: routeName }, '*');
  }
}

export function getUserDataForIntegration(payload: {
  registerType: string;
  subscriberId?: string;
}) {
  const { registerType, subscriberId } = payload || {};
  const hashedSubscriberId = subscriberId ? sha256(subscriberId) : undefined;
  const isEmail = subscriberId && (''+subscriberId.includes('@') || ''+subscriberId.includes('@privaterelay.appleid.com'));
  const registerFinalType = registerType === 'other'
    ? isEmail ? 'email' : undefined
    : registerType;

  const userData: Record<string, any> = {
    ...(registerFinalType && hashedSubscriberId ? {[registerFinalType === 'email' ? 'em' : 'ph']: hashedSubscriberId} : {}),
  }

  const context = {
    ...(registerFinalType && hashedSubscriberId ? {[registerFinalType === 'email' ? 'email' : 'phone_number']: hashedSubscriberId} : {})
  }

  return {
    user_data: userData,
    context
  }
}

export function eventRegisterSuccess(payload?: {
  registerType: string;
  subscriberId?: string;
}) {
  const { user_data: userData, context } = getUserDataForIntegration(payload!);

  Facebook.track('CompleteRegistration', {
    pageType: 'Registration',
    pageSlug: globalThis?.location?.pathname,
    ...userData
  });

  Tiktok.identify(context);
  Tiktok.track('CompleteRegistration', {
    pageType: 'Registration',
    pageSlug: globalThis?.location?.pathname,
    ...context
  });
}


export function areIntegrationsLoaded() {
  const integrationList = []
  if (!GTM.options.debug && GTM.options.id) integrationList.push('GTM');
  if (!GA4.options.debug && (GA4.options.gaCode || GA4.options.googleAds)) integrationList.push('GA4');
  if (!Facebook.options.debug && Facebook.options.id) integrationList.push('FB');
  if (!Tiktok.options.debug && Tiktok.options.id) integrationList.push('Tiktok');
  if (integrationList.length === 0) return true;

  const loaded = (globalThis as any)?.loadedIntegrations || [];
  return integrationList.every((integration) => loaded.includes(integration));
}

// Make sure all integrations are loaded before proceeding
export async function waitForIntegrations() {
  let tryCount = 0;
  while (!areIntegrationsLoaded() && tryCount < 100) {
    tryCount++;
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  return true;
}
